import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAuth } from '../../contexts/AuthContext';
import { useHtmlBackground } from '../../hooks/useHtmlBackground';
import { api } from '../../services/api';
import { getOrCreateAnonymousId } from '../../services/authStorage';
import { features } from '../../config/features';

import logoSvg from '../../../assets/splash/Logo.svg';
import illustrationSvg from '../../../assets/login-screen/login-landing-asset.svg';
import gradientOverlaySvg from '../../../assets/login-screen/landing-background-gradient.svg';

export default function LoginV2() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const [isGuestLoading, setIsGuestLoading] = useState(false);

  useHtmlBackground('#DBF7F8');

  if (!authLoading && isAuthenticated) {
    navigate('/home', { replace: true });
    return null;
  }

  if (authLoading) {
    return null;
  }

  const handleGetStarted = () => {
    navigate('/login/email', { state: { intent: 'get_started' } });
  };

  const handleExistingAccount = () => {
    navigate('/login/email', { state: { intent: 'existing_account' } });
  };

  const handleExploreAsGuest = async () => {
    setIsGuestLoading(true);
    try {
      const anonymousId = getOrCreateAnonymousId();
      const response = await api.guestLogin({ anonymousId });
      login(response);
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Guest login failed:', error);
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsGuestLoading(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #00A3D4 13%, #06A9C9 27.6%, #DBF7F8 66.3%)',
      }}
    >
      {/* Gradient overlay - page-level background layer */}
      <img
        src={gradientOverlaySvg}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      <div className="relative max-w-[430px] mx-auto w-full h-full flex flex-col items-center overflow-hidden">
        {/* Top spacer */}
        <div className="h-[8%]" />

        {/* Logo */}
        <motion.div variants={itemVariants} className="flex justify-center w-full">
          <img
            src={logoSvg}
            alt="Play Moments"
            className="w-[75.8%] max-w-[326px] h-auto"
          />
        </motion.div>

        {/* Illustration + Text - grouped together, centered vertically */}
        <div className="flex-1 flex flex-col items-center justify-center w-full min-h-0">
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center w-full"
          >
            <img
              src={illustrationSvg}
              alt="Cloud and star characters"
              className="w-[72.8%] max-w-[382px] h-auto"
            />
          </motion.div>

          <motion.div variants={itemVariants} className="w-[88.8%] max-w-[382px] text-center mt-4">
            <h1
              className="font-black text-[25px] leading-tight mb-5"
              style={{ color: '#1B2831', letterSpacing: '-1px' }}
            >
              Daily play ideas, made simple
            </h1>
            <p
              className="text-[14px] leading-normal"
              style={{ color: '#1B2831' }}
            >
              Simple screen-free activities to help your child talk, play and grow
              – one small moment at a time.
            </p>
          </motion.div>
        </div>

        {/* Buttons */}
        <motion.div
          variants={itemVariants}
          className="w-[88.8%] max-w-[382px] flex flex-col gap-2 mb-[8vh]"
          style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}
        >
          {features.showLogin && (
            <>
              <button
                onClick={handleGetStarted}
                className="w-full h-12 rounded-xl uppercase text-[12px] font-black tracking-[0.72px]"
                style={{ backgroundColor: '#1B2831', color: '#FFFFFF' }}
              >
                GET STARTED
              </button>
              <button
                onClick={handleExistingAccount}
                className="w-full h-12 rounded-xl uppercase text-[12px] font-black tracking-[0.72px]"
                style={{
                  backgroundColor: 'transparent',
                  border: '2px solid #1B2831',
                  color: '#1B2831',
                }}
              >
                I ALREADY HAVE AN ACCOUNT
              </button>
            </>
          )}
          {features.showGuestLogin && (
            <button
              onClick={handleExploreAsGuest}
              disabled={isGuestLoading}
              className="w-full h-12 rounded-xl uppercase text-[12px] font-black tracking-[0.72px] disabled:opacity-70"
              style={{ backgroundColor: '#1B2831', color: '#FFFFFF' }}
            >
              {isGuestLoading ? 'LOADING...' : 'EXPLORE AS GUEST'}
            </button>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
